/**
 * Login Page component for Azure AD authentication using MSAL.
 *
 * Simple single-button login flow:
 * 1. User clicks "Sign in with Microsoft"
 * 2. Azure AD popup handles authentication (including Home Realm Discovery)
 * 3. Backend determines user type from token claims
 *
 * Uses Fluent UI components for consistent styling with the rest of the app.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Text,
  Link,
  Spinner,
  makeStyles,
  tokens,
  mergeClasses,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAuth, LAST_USER_EMAIL_KEY } from '../contexts/AuthContext';

// Microsoft Logo SVG component
const MicrosoftLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" style={{ width: 20, height: 20 }}>
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const useStyles = makeStyles({
  // Full-page container with gradient mesh background
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: tokens.spacingVerticalXXL,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground3,
    position: 'relative',
    overflow: 'hidden',
  },

  // Animated gradient mesh
  gradientMesh: {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `
      radial-gradient(ellipse at 20% 20%, color-mix(in srgb, ${tokens.colorBrandBackground} 12%, transparent) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 80%, color-mix(in srgb, ${tokens.colorBrandBackground} 8%, transparent) 0%, transparent 50%),
      radial-gradient(ellipse at 40% 70%, color-mix(in srgb, ${tokens.colorBrandBackground} 6%, transparent) 0%, transparent 40%)`,
    animationName: {
      '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
      '33%': { transform: 'translate(2%, 1%) rotate(1deg)' },
      '66%': { transform: 'translate(-1%, 2%) rotate(-1deg)' },
    },
    animationDuration: '20s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    pointerEvents: 'none',
  },

  // Subtle grid pattern overlay
  gridPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(${tokens.colorNeutralStroke2} 1px, transparent 1px),
      linear-gradient(90deg, ${tokens.colorNeutralStroke2} 1px, transparent 1px)`,
    backgroundSize: '60px 60px',
    opacity: 0.3,
    pointerEvents: 'none',
  },

  // Main card container
  card: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '16px',
    boxShadow: `
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 20px 40px -10px rgba(0, 0, 0, 0.15),
      0 0 0 1px ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
    // Subtle animation on mount
    animationName: {
      from: {
        opacity: 0,
        transform: 'translateY(8px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    animationDuration: '0.4s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'both',
  },

  // Hero section for logo
  logoHero: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '40px 48px 16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  logo: {
    maxWidth: '240px',
    height: 'auto',
  },

  // Content area below hero
  content: {
    padding: '32px 48px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  // Subtitle text
  subtitle: {
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
    lineHeight: tokens.lineHeightBase400,
    marginBottom: tokens.spacingVerticalM,
  },

  // Welcome back section for returning users
  welcomeSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
  },

  welcomeTitle: {
    color: tokens.colorNeutralForeground1,
  },

  userEmail: {
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginTop: tokens.spacingVerticalM,
  },

  // Sign in button
  signInButton: {
    width: '100%',
    height: '52px',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: '10px',
    transition: 'all 0.2s ease',
    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: tokens.shadow8,
    },
    ':active': {
      transform: 'translateY(0)',
    },
  },

  // Microsoft button style (white background with stronger shadow)
  microsoftButton: {
    backgroundColor: '#ffffff',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: '#1e293b',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.08)',
    ':hover': {
      backgroundColor: '#f8fafc',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.1)',
    },
  },

  // Button content layout
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
  },

  // "Use different account" link
  differentAccountLink: {
    textAlign: 'center',
    paddingTop: tokens.spacingVerticalXS,
  },

  // Footer section
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  footerText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },

  // Error message styling
  errorMessage: {
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalS,
    maxWidth: '100%',
    '& *': {
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
    },
  },
});

/**
 * Login Page component.
 *
 * Renders the login page with a single "Sign in with Microsoft" button.
 * Azure AD handles Home Realm Discovery automatically.
 * Redirects to home if user is already authenticated.
 */
export function LoginPage(): JSX.Element {
  const styles = useStyles();
  const { login, isAuthenticated, isLoading, isSsoAttempting, authError, clearAuthError, switchAccount, user } = useAuth();
  const isMsalAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<string>('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [storedEmail, setStoredEmail] = useState<string | null>(null);
  const [forceAccountSelection, setForceAccountSelection] = useState(false);

  // Show verifying state when MSAL auth completed but backend validation is in progress
  const isVerifying = isMsalAuthenticated && isLoading;

  // Check for stored email (returning user) on mount
  useEffect(() => {
    const email = localStorage.getItem(LAST_USER_EMAIL_KEY);
    setStoredEmail(email);
  }, []);


  // Store email and redirect to home if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Store email for "Welcome back" experience on future visits (if available)
      if (user?.email) {
        localStorage.setItem(LAST_USER_EMAIL_KEY, user.email);
      }
      navigate('/app', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  const handleLogin = async () => {
    setLoginError('');
    clearAuthError();
    setIsLoggingIn(true);
    try {
      // Pass stored email as loginHint for returning users
      // If forceAccountSelection is true, pass it to force account picker
      await login(storedEmail ?? undefined, forceAccountSelection);
      setForceAccountSelection(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Login failed:', error);
      setLoginError(error instanceof Error ? error.message : 'Login failed. Please try again.');
      setForceAccountSelection(false);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleUseDifferentAccount = () => {
    localStorage.removeItem(LAST_USER_EMAIL_KEY);
    setStoredEmail(null);
    setForceAccountSelection(true);
  };

  const renderButtonContent = (text: string, showMicrosoftLogo = false) => {
    if (isLoggingIn) {
      return (
        <span className={styles.buttonContent}>
          <Spinner size="tiny" />
          <span>Signing in...</span>
        </span>
      );
    }
    if (isVerifying) {
      return (
        <span className={styles.buttonContent}>
          <Spinner size="tiny" />
          <span>Verifying account...</span>
        </span>
      );
    }
    return (
      <span className={styles.buttonContent}>
        {showMicrosoftLogo && <MicrosoftLogo />}
        <span>{text}</span>
      </span>
    );
  };

  // Show spinner during silent SSO attempt
  if (isSsoAttempting) {
    return (
      <div className={styles.page}>
        <div className={styles.gradientMesh} aria-hidden="true" />
        <div className={styles.gridPattern} aria-hidden="true" />
        <div className={styles.card} data-testid="login-card" role="main">
          <div className={styles.logoHero}>
            <img src="/irpilot-logo.svg" alt="IRPilot" className={styles.logo} />
          </div>
          <div className={styles.content}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <Spinner size="large" />
              <Text size={400}>Signing you in...</Text>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Animated gradient mesh background */}
      <div className={styles.gradientMesh} aria-hidden="true" />
      {/* Subtle grid pattern overlay */}
      <div className={styles.gridPattern} aria-hidden="true" />

      {/* Main login card */}
      <div className={styles.card} data-testid="login-card" role="main">
        {/* Dark hero section with logo */}
        <div className={styles.logoHero}>
          <img
            src="/irpilot-logo.svg"
            alt="IRPilot"
            className={styles.logo}
          />
        </div>

        {/* Content area */}
        <div className={styles.content}>
          <Text size={400} className={styles.subtitle}>
            Secure out-of-band communication management for Microsoft 365 tenants
          </Text>

          {/* Error messages */}
          {(loginError || authError) && (
            <MessageBar intent="error" className={styles.errorMessage}>
              <MessageBarBody>{loginError || authError}</MessageBarBody>
            </MessageBar>
          )}

          {/* Show "Try different account" button when user is not registered */}
          {authError?.includes('not registered') && (
            <Button
              appearance="secondary"
              size="medium"
              onClick={switchAccount}
            >
              Try a different account
            </Button>
          )}

          {storedEmail ? (
            // Returning user: Welcome back experience
            <>
              <div className={styles.welcomeSection}>
                <Text size={500} weight="semibold" className={styles.welcomeTitle}>
                  Welcome back
                </Text>
                <Text className={styles.userEmail}>
                  {storedEmail}
                </Text>
              </div>

              <Button
                appearance="primary"
                size="large"
                className={styles.signInButton}
                onClick={handleLogin}
                disabled={isLoggingIn || isLoading || isVerifying}
              >
                {renderButtonContent('Continue')}
              </Button>

              <div className={styles.differentAccountLink}>
                <Link onClick={handleUseDifferentAccount}>
                  Use a different account
                </Link>
              </div>
            </>
          ) : (
            // First-time visitor: Standard Microsoft sign in
            <Button
              appearance="secondary"
              size="large"
              className={mergeClasses(styles.signInButton, styles.microsoftButton)}
              onClick={handleLogin}
              disabled={isLoggingIn || isLoading || isVerifying}
            >
              {renderButtonContent('Sign in with Microsoft', true)}
            </Button>
          )}

          {/* Footer */}
          <div className={styles.footer}>
            <Text className={styles.footerText}>
              Protected by Microsoft Entra ID
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
